import { supabase } from '../lib/supabase';

const meetingService = {
  /**
   * Create a new meeting with automated reminder scheduling
   */
  async createMeeting(meetingData) {
    try {
      const {
        teamId,
        organizerId,
        title,
        description,
        meetingType,
        date,
        time,
        duration,
        locationType,
        locationDetails,
        googleMeetLink,
        attendeeIds
      } = meetingData;

      // Validate required fields
      if (!title || !date || !time || !organizerId) {
        throw new Error('Missing required meeting fields');
      }

      // FIXED: Handle null teamId for PCF Admin organization-wide meetings
      const meetingPayload = {
        organizer_id: organizerId,
        title,
        description,
        meeting_type: meetingType,
        meeting_date: date,
        meeting_time: time,
        duration_minutes: duration,
        location_type: locationType,
        location_details: locationDetails,
        google_meet_link: googleMeetLink,
        attendee_ids: attendeeIds || [],
        status: 'scheduled'
      };

      // Only include team_id if it's provided (not null)
      if (teamId) {
        meetingPayload.team_id = teamId;
      }

      // CRITICAL FIX: Insert meeting without triggering any realtime subscriptions
      // that could cause connection drops
      const { data: meeting, error: meetingError } = await supabase
        ?.from('meetings')
        ?.insert(meetingPayload)
        ?.select()
        ?.single();

      if (meetingError) throw meetingError;

      // CRITICAL: Send notifications asynchronously without blocking
      // This prevents the connection from dropping during notification sending
      if (attendeeIds && attendeeIds?.length > 0) {
        // Use setTimeout to send notifications completely asynchronously
        // This ensures the meeting creation returns immediately
        setTimeout(async () => {
          try {
            // Fetch full attendee details for email notification
            const { data: attendeeDetails } = await supabase
              ?.from('user_profiles')
              ?.select('id, full_name, email, notification_email')
              ?.in('id', attendeeIds);

            // Fetch organizer details
            const { data: organizerDetails } = await supabase
              ?.from('user_profiles')
              ?.select('full_name, email')
              ?.eq('id', organizerId)
              ?.single();

            const attendeesForEmail = attendeeDetails?.map(attendee => ({
              id: attendee?.id,
              name: attendee?.full_name,
              email: attendee?.notification_email || attendee?.email
            }));

            const meetingDetailsForEmail = {
              id: meeting?.id,
              title,
              description,
              date,
              time,
              duration,
              location: locationType,
              locationDetails,
              googleMeetLink,
              organizer: organizerDetails?.full_name,
              organizerEmail: organizerDetails?.email
            };

            // Call send-meeting-notification edge function for immediate notification
            const { error: notificationError } = await supabase?.functions?.invoke('send-meeting-notification', {
              body: {
                attendees: attendeesForEmail,
                meetingDetails: meetingDetailsForEmail,
                notificationType: 'new_meeting'
              }
            });

            if (notificationError) {
              console.error('Failed to send immediate meeting notification:', notificationError);
            } else {
              console.log('✅ Meeting created successfully');
              console.log('📧 Immediate notification sent to attendees');
              console.log('⏰ Automated reminders will be sent 24 hours and 1 hour before the meeting');
            }
          } catch (emailError) {
            console.error('Error sending meeting notifications:', emailError);
            // Don't throw - meeting creation was successful
          }
        }, 0);
      }

      return { data: meeting, error: null };
    } catch (err) {
      console.error('Error creating meeting:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Cancel a meeting and notify all attendees
   */
  async cancelMeeting(meetingId, organizerId) {
    try {
      // Fetch meeting details before cancellation
      const { data: meeting, error: fetchError } = await supabase
        ?.from('meetings')
        ?.select('*, organizer:user_profiles!meetings_organizer_id_fkey(full_name, email)')
        ?.eq('id', meetingId)
        ?.single();

      if (fetchError) throw fetchError;

      // Verify the user is the organizer
      if (meeting?.organizer_id !== organizerId) {
        throw new Error('Only the meeting organizer can cancel this meeting');
      }

      // Update meeting status to cancelled
      const { error: updateError } = await supabase
        ?.from('meetings')
        ?.update({ status: 'cancelled' })
        ?.eq('id', meetingId);

      if (updateError) throw updateError;

      // Send notifications asynchronously
      if (meeting?.attendee_ids && meeting?.attendee_ids?.length > 0) {
        Promise.resolve()?.then(async () => {
          try {
            // Fetch attendee details
            const { data: attendeeDetails } = await supabase
              ?.from('user_profiles')
              ?.select('id, full_name, email, notification_email')
              ?.in('id', meeting?.attendee_ids);

            // Create in-app notifications for each attendee
            const notificationPromises = attendeeDetails?.map(attendee => 
              supabase?.from('notifications')?.insert({
                user_id: attendee?.id,
                team_id: meeting?.team_id,
                type: 'meeting',
                title: 'Meeting Cancelled',
                message: `The meeting "${meeting?.title}" scheduled for ${meeting?.meeting_date} at ${meeting?.meeting_time} has been cancelled by ${meeting?.organizer?.full_name}.`,
                reference_id: meetingId,
                reference_type: 'meeting',
                is_read: false
              })
            );

            await Promise.all(notificationPromises);

            // Send email notifications
            const attendeesForEmail = attendeeDetails?.map(attendee => ({
              id: attendee?.id,
              name: attendee?.full_name,
              email: attendee?.notification_email || attendee?.email
            }));

            const meetingDetailsForEmail = {
              id: meeting?.id,
              title: meeting?.title,
              description: meeting?.description,
              date: meeting?.meeting_date,
              time: meeting?.meeting_time,
              duration: meeting?.duration_minutes,
              location: meeting?.location_type,
              locationDetails: meeting?.location_details,
              googleMeetLink: meeting?.google_meet_link,
              organizer: meeting?.organizer?.full_name,
              organizerEmail: meeting?.organizer?.email
            };

            await supabase?.functions?.invoke('send-meeting-notification', {
              body: {
                attendees: attendeesForEmail,
                meetingDetails: meetingDetailsForEmail,
                notificationType: 'meeting_cancelled'
              }
            });

            console.log('✅ Meeting cancelled and notifications sent');
          } catch (notifError) {
            console.error('Error sending cancellation notifications:', notifError);
          }
        });
      }

      return { data: { success: true }, error: null };
    } catch (err) {
      console.error('Error cancelling meeting:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Get meetings where user is an attendee or organizer
   */
  async getUserMeetings(userId, teamId = null) {
    try {
      let query = supabase
        ?.from('meetings')
        ?.select('*')
        ?.order('meeting_date', { ascending: true })
        ?.order('meeting_time', { ascending: true });

      // Get meetings where user is organizer OR in attendee_ids array
      query = query?.or(`organizer_id.eq.${userId},attendee_ids.cs.{${userId}}`);

      // Optionally filter by team
      if (teamId) {
        query = query?.eq('team_id', teamId);
      }

      const { data: meetings, error: meetingsError } = await query;

      if (meetingsError) throw meetingsError;

      // Fetch attendee details for each meeting
      const meetingsWithAttendees = await Promise.all(
        (meetings || [])?.map(async (meeting) => {
          if (meeting?.attendee_ids && meeting?.attendee_ids?.length > 0) {
            const { data: attendees } = await supabase
              ?.from('user_profiles')
              ?.select('id, full_name, email, role')
              ?.in('id', meeting?.attendee_ids);

            return { ...meeting, attendees: attendees || [] };
          }
          return { ...meeting, attendees: [] };
        })
      );

      return { data: meetingsWithAttendees, error: null };
    } catch (err) {
      console.error('Error fetching user meetings:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Get team meetings with attendee details
   */
  async getTeamMeetings(teamId) {
    try {
      // Fetch meetings for the team OR organization-wide meetings (null team_id)
      let query = supabase
        ?.from('meetings')
        ?.select('*')
        ?.order('meeting_date', { ascending: true })
        ?.order('meeting_time', { ascending: true });

      // If teamId is provided, get team-specific meetings
      // If teamId is null (PCF Admin), get all organization-wide meetings
      if (teamId) {
        query = query?.eq('team_id', teamId);
      } else {
        query = query?.is('team_id', null);
      }

      const { data: meetings, error: meetingsError } = await query;

      if (meetingsError) throw meetingsError;

      // Fetch attendee details for each meeting
      const meetingsWithAttendees = await Promise.all(
        meetings?.map(async (meeting) => {
          if (meeting?.attendee_ids && meeting?.attendee_ids?.length > 0) {
            const { data: attendees } = await supabase
              ?.from('user_profiles')
              ?.select('id, full_name, email')
              ?.in('id', meeting?.attendee_ids);

            return {
              ...meeting,
              attendees: attendees || []
            };
          }
          return {
            ...meeting,
            attendees: []
          };
        })
      );

      return { data: meetingsWithAttendees, error: null };
    } catch (err) {
      console.error('Error fetching team meetings:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Updates an existing meeting
   * @param {string} meetingId - Meeting ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated meeting
   */
  async updateMeeting(meetingId, updates) {
    try {
      const { data, error } = await supabase?.from('meetings')?.update(updates)?.eq('id', meetingId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating meeting:', error);
      return { data: null, error };
    }
  },

  /**
   * Deletes a meeting
   * @param {string} meetingId - Meeting ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteMeeting(meetingId) {
    try {
      const { error } = await supabase?.from('meetings')?.delete()?.eq('id', meetingId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting meeting:', error);
      return { error };
    }
  },

  /**
   * Gets upcoming meetings for current user
   * @returns {Promise<Array>} List of upcoming meetings
   */
  async getUpcomingMeetings() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('No authenticated user');

      const now = new Date();
      const today = now?.toISOString()?.split('T')?.[0];
      const currentTime = now?.toTimeString()?.split(' ')?.[0];

      const { data, error } = await supabase?.from('meetings')?.select(`
        *,
        organizer:user_profiles!organizer_id(full_name, email)
      `)?.or(`organizer_id.eq.${user?.id},attendee_ids.cs.{${user?.id}}`)?.eq('status', 'scheduled')?.or(`meeting_date.gt.${today},and(meeting_date.eq.${today},meeting_time.gte.${currentTime})`)?.order('meeting_date', { ascending: true })?.order('meeting_time', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching upcoming meetings:', error);
      return { data: null, error };
    }
  }
};

export default meetingService;